
import React, { useEffect, useState } from "react";
import axios from "axios";
import "./MISSendEmailModal.css";

const API = `${process.env.REACT_APP_BASE_URL}/api/mis/email`;

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const monthTitle = (month) => {
  if (!/^\d{4}-\d{2}$/.test(month || "")) return month || "";

  const [year, monthNumber] = month.split("-").map(Number);

  return new Date(year, monthNumber - 1, 1).toLocaleString("en-IN", {
    month: "long",
    year: "numeric",
  });
};

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_TOTAL_SIZE = 20 * 1024 * 1024;

// Extract email addresses from strings such as:
// John Doe <john@example.com>, jane@example.com
const extractEmails = (value) => {
  if (!value) return [];

  const values = Array.isArray(value) ? value : [value];
  const emailRegex = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

  return values.flatMap((item) => {
    if (typeof item === "string") {
      return item.match(emailRegex) || [];
    }

    // Also support APIs returning { email: "..." } objects.
    if (item && typeof item === "object") {
      const address =
        item.email || item.address || item.value || "";

      return String(address).match(emailRegex) || [];
    }

    return [];
  });
};

// Remove duplicate addresses and exclude the connected Gmail account.
const uniqueEmails = (emails, ownEmail = "") => {
  const own = ownEmail.trim().toLowerCase();
  const unique = new Map();

  emails.forEach((email) => {
    const address = String(email).trim();
    const key = address.toLowerCase();

    if (!key || key === own) return;

    if (!unique.has(key)) {
      unique.set(key, address);
    }
  });

  return Array.from(unique.values());
};

export default function InvoiceSendEmailModal({
  clientId,
  clientName,
  month,
  onClose,
  onSent,
}) {
  const [status, setStatus] = useState({
    loading: true,
    connected: false,
    email: "",
  });

  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [conversations, setConversations] = useState([]);
  const [threadMessageId, setThreadMessageId] = useState("");
  const [loadingConversations, setLoadingConversations] = useState(false);

  const [subject, setSubject] = useState(
    `Invoice – ${clientName} – ${monthTitle(month)}`
  );

  const [message, setMessage] = useState(
    `Dear ${clientName},\n\nPlease see the details below regarding ${monthTitle(month)}.\n\nRegards`
  );

  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  // Check Google connection.
  useEffect(() => {
    let alive = true;

    axios
      .get(`${API}/invoice/google/status`, {
        headers: authHeaders(),
      })
      .then(({ data }) => {
        if (!alive) return;

        setStatus({
          loading: false,
          connected: Boolean(data.connected),
          email: data.email || "",
        });
      })
      .catch((e) => {
        if (!alive) return;

        setStatus({
          loading: false,
          connected: false,
          email: "",
        });

        setError(
          e.response?.data?.message ||
            "Unable to check Google connection."
        );
      });

    return () => {
      alive = false;
    };
  }, []);

  // Load conversations from the connected accounts mailbox.
  useEffect(() => {
    let alive = true;

    setLoadingConversations(true);

    axios
      .get(`${API}/inbox/accounts`, {
        headers: authHeaders(),
        params: { maxResults: 50 },
      })
      .then(({ data }) => {
        if (alive) {
          setConversations(data.messages || []);
        }
      })
      .catch((e) => {
        if (!alive) return;

        setError(
          e.response?.data?.message ||
            "Unable to load conversations from the accounts Gmail inbox."
        );
      })
      .finally(() => {
        if (alive) setLoadingConversations(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  // Connect or reconnect Google.
  const connectGoogle = async () => {
    setError("");

    try {
      const { data } = await axios.get(
        `${API}/invoice/google/connect`,
        { headers: authHeaders() }
      );

      if (!data.url) {
        throw new Error(
          "Server did not return a Google authorization URL."
        );
      }

      window.location.assign(data.url);
    } catch (e) {
      setError(
        e.response?.data?.message ||
          e.message ||
          "Could not connect Google."
      );
    }
  };

  // Select a conversation and populate Reply All recipients.
  const chooseConversation = async (messageId) => {
    setThreadMessageId(messageId);
    setError("");

    // Clear the previous conversation's recipients immediately.
    setTo("");
    setCc("");

    if (!messageId) return;

    try {
      const { data } = await axios.get(
        `${API}/inbox/accounts/${encodeURIComponent(messageId)}`,
        { headers: authHeaders() }
      );

      const original = data.message;

      if (!original) {
        throw new Error(
          "The selected email was not returned by the server."
        );
      }

      // Use the original conversation subject.
      if (original.subject) {
        setSubject(original.subject);
      }

      const ownEmail = String(status.email || "").trim();

      // Read the original sender, To and CC headers.
      const senderEmails = extractEmails(original.from);
      const originalToEmails = extractEmails(original.to);
      const originalCcEmails = extractEmails(original.cc);

      /*
       * Reply All logic:
       *
       * To:
       *   - Original sender
       *   - Original To recipients
       *
       * CC:
       *   - Original CC recipients
       *
       * Both:
       *   - Exclude the connected Gmail account
       *   - Remove duplicate addresses
       *
       * If an address is already in To, it is removed from CC.
       */

      const toRecipients = uniqueEmails(
        [...senderEmails, ...originalToEmails],
        ownEmail
      );

      const ccRecipients = uniqueEmails(
        originalCcEmails,
        ownEmail
      ).filter(
        (ccEmail) =>
          !toRecipients.some(
            (toEmail) =>
              toEmail.toLowerCase() === ccEmail.toLowerCase()
          )
      );

      setTo(toRecipients.join(", "));
      setCc(ccRecipients.join(", "));
    } catch (e) {
      setThreadMessageId("");
      setTo("");
      setCc("");

      setError(
        e.response?.data?.message ||
          e.message ||
          "Could not open the selected conversation in the accounts mailbox."
      );
    }
  };

  // Add attachments with size validation.
  const addFiles = (event) => {
    const selected = Array.from(event.target.files || []);
    const combined = [...files, ...selected];

    if (selected.some((file) => file.size > MAX_FILE_SIZE)) {
      setError("Each attachment must be 10 MB or smaller.");
      event.target.value = "";
      return;
    }

    if (
      combined.reduce((sum, file) => sum + file.size, 0) >
      MAX_TOTAL_SIZE
    ) {
      setError(
        "All attachments together must be 20 MB or smaller."
      );
      event.target.value = "";
      return;
    }

    setError("");

    setFiles(
      combined.filter(
        (file, index, array) =>
          array.findIndex(
            (item) =>
              item.name === file.name &&
              item.size === file.size &&
              item.lastModified === file.lastModified
          ) === index
      )
    );

    event.target.value = "";
  };

  // Send the invoice email.
  const send = async (event) => {
    event.preventDefault();
    setError("");

    if (!threadMessageId) {
      setError("Please select an existing conversation.");
      return;
    }

    if (!to.trim()) {
      setError("Please enter at least one To recipient.");
      return;
    }

    setSending(true);

    try {
      const attachments = await Promise.all(
        files.map(
          (file) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();

              reader.onload = () => {
                resolve({
                  filename: file.name,
                  mimeType:
                    file.type || "application/octet-stream",
                  contentBase64:
                    String(reader.result).split(",")[1] || "",
                });
              };

              reader.onerror = () => {
                reject(
                  new Error(`Could not read ${file.name}.`)
                );
              };

              reader.readAsDataURL(file);
            })
        )
      );

      await axios.post(
        `${API}/send-custom`,
        {
          clientId,
          month,
          to,
          cc,
          subject,
          message,
          attachments,
          purpose: "accounts",
          threadMessageId,
        },
        { headers: authHeaders() }
      );

      await onSent?.();
      onClose?.();
    } catch (e) {
      setError(
        e.response?.data?.message ||
          e.message ||
          "Email could not be sent. Please try again."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="mis-email-backdrop"
      role="presentation"
      onMouseDown={(event) =>
        event.target === event.currentTarget &&
        !sending &&
        onClose?.()
      }
    >
      <section
        className="mis-email-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-email-title"
      >
        <header className="mis-email-header">
          <div>
            <h2 id="invoice-email-title">
              Send Invoice in Existing Conversation
            </h2>

            <p>
              {clientName} · {monthTitle(month)}
            </p>
          </div>

          <button
            type="button"
            className="mis-email-close"
            onClick={onClose}
            disabled={sending}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <form onSubmit={send}>
          <div className="mis-email-content">
            <div className="mis-email-google">
              <div>
                <strong>
                  {status.loading
                    ? "Checking Google connection…"
                    : status.connected
                    ? `Connected: ${status.email}`
                    : "Google account not connected"}
                </strong>

                <small>
                  Invoice will be sent from the connected accounts
                  Gmail account.
                </small>
              </div>

              <button
                type="button"
                className="mis-email-secondary"
                onClick={connectGoogle}
                disabled={status.loading || sending}
              >
                {status.connected
                  ? "Reconnect Google"
                  : "Connect Google"}
              </button>
            </div>

            <label>
              Existing MIS conversation <span>*</span>

              <select
                required
                value={threadMessageId}
                onChange={(event) =>
                  chooseConversation(event.target.value)
                }
                disabled={
                  loadingConversations ||
                  sending ||
                  !status.connected
                }
              >
                <option value="">
                  {loadingConversations
                    ? "Loading conversations…"
                    : "Select the MIS conversation in the accounts mailbox"}
                </option>

                {conversations.map((item) => (
                  <option key={item.id} value={item.id}>
                    {`${item.subject || "(No subject)"} — ${
                      item.from || "Unknown sender"
                    } — ${
                      item.date
                        ? new Date(item.date).toLocaleDateString(
                            "en-IN"
                          )
                        : ""
                    }`}
                  </option>
                ))}
              </select>

              <small>
                Select the original email. The To and CC fields
                will be populated using Reply All recipient logic.
              </small>
            </label>

            <label>
              To <span>*</span>

              <input
                required
                value={to}
                onChange={(event) => setTo(event.target.value)}
                placeholder="client@example.com"
                disabled={sending}
              />

              <small>
                Original sender and To recipients, excluding your
                connected Gmail account. Separate multiple addresses
                with commas or semicolons.
              </small>
            </label>

            <label>
              CC

              <input
                value={cc}
                onChange={(event) => setCc(event.target.value)}
                placeholder="accounts@example.com"
                disabled={sending}
              />

              <small>
                Original CC recipients. Duplicate addresses are
                removed automatically.
              </small>
            </label>

            <label>
              Subject <span>*</span>

              <input
                required
                value={subject}
                onChange={(event) =>
                  setSubject(event.target.value)
                }
                maxLength={250}
                disabled={sending}
              />
            </label>

            <label>
              Message <span>*</span>

              <textarea
                required
                rows={7}
                value={message}
                onChange={(event) =>
                  setMessage(event.target.value)
                }
                maxLength={20000}
                disabled={sending}
              />
            </label>

            <div className="mis-email-attachments">
              <strong>Attachments (optional)</strong>

              <p>
                No files are attached automatically. Choose any
                files you want to include.
              </p>

              <input
                type="file"
                multiple
                onChange={addFiles}
                aria-label="Add email attachments"
                disabled={sending}
              />

              {files.length > 0 && (
                <ul>
                  {files.map((file, index) => (
                    <li
                      key={`${file.name}-${file.size}-${file.lastModified}`}
                    >
                      {file.name} (
                      {(file.size / 1024).toFixed(0)} KB){" "}

                      <button
                        type="button"
                        className="mis-email-secondary"
                        onClick={() =>
                          setFiles((old) =>
                            old.filter((_, i) => i !== index)
                          )
                        }
                        disabled={sending}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <small>
                Maximum 10 MB per file and 20 MB total.
              </small>
            </div>

            {error && (
              <div className="mis-email-error" role="alert">
                {error}
              </div>
            )}
          </div>

          <footer className="mis-email-footer">
            <button
              type="button"
              className="mis-email-secondary"
              onClick={onClose}
              disabled={sending}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="mis-email-primary"
              disabled={
                sending ||
                status.loading ||
                !status.connected ||
                !threadMessageId
              }
            >
              {sending ? "Sending…" : "Send Invoice"}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}
